/**
 * Utility functions for comparing and diffing objects
 */

/**
 * Compute a diff between two objects and return a list of changes
 * @param {Object} oldObj - The previous object
 * @param {Object} newObj - The new object
 * @param {Object} options - Optional configuration
 * @param {string} options.rootPath - The root path name for the object (default: 'root')
 * @param {number} options.maxArrayItems - Maximum number of array items to check (default: 3)
 * @param {number} options.truncateLength - Max length for displayed values (default: 50)
 * @returns {Array} Array of change descriptions
 */
export const computeDiff = (oldObj, newObj, options = {}) => {
  if (!oldObj) return ['Initial state (no previous state)'];
  
  const {
    rootPath = 'root',
    maxArrayItems = 3,
    truncateLength = 50
  } = options;
  
  const changes = [];
  const processObject = (path, oldVal, newVal) => {
    // Skip functions
    if (typeof oldVal === 'function' || typeof newVal === 'function') {
      return;
    }
    
    // Handle arrays
    if (Array.isArray(oldVal) && Array.isArray(newVal)) {
      if (oldVal.length !== newVal.length) {
        changes.push(`${path}.length: ${oldVal.length} → ${newVal.length}`);
      }
      
      // Check first few array elements
      const maxCheck = Math.min(oldVal.length, newVal.length, maxArrayItems);
      for (let i = 0; i < maxCheck; i++) {
        processObject(`${path}[${i}]`, oldVal[i], newVal[i]);
      }
      return;
    }
    
    // Handle objects (recursively)
    if (typeof oldVal === 'object' && oldVal !== null && 
        typeof newVal === 'object' && newVal !== null) {
      // Get all keys from both objects
      const keys = new Set([...Object.keys(oldVal), ...Object.keys(newVal)]);
      
      for (const key of keys) {
        // Skip keys that start with _ to avoid logging internal properties
        if (key.startsWith('_')) continue;
        
        // Check if key exists in both objects
        if (!(key in oldVal)) {
          changes.push(`${path}.${key}: added (${JSON.stringify(newVal[key]).substring(0, truncateLength)})`);
        } else if (!(key in newVal)) {
          changes.push(`${path}.${key}: removed`);
        } else {
          processObject(`${path}.${key}`, oldVal[key], newVal[key]);
        }
      }
      return;
    }
    
    // Handle primitive values
    if (oldVal !== newVal) {
      // For simple values, show the before and after
      const oldStr = typeof oldVal === 'string' ? oldVal : JSON.stringify(oldVal);
      const newStr = typeof newVal === 'string' ? newVal : JSON.stringify(newVal);
      
      // Truncate long values
      const truncate = (str) => 
        str && str.length > truncateLength ? `${str.substring(0, truncateLength - 3)}...` : str;
      
      changes.push(`${path}: ${truncate(oldStr)} → ${truncate(newStr)}`);
    }
  };
  
  processObject(rootPath, oldObj, newObj);
  return changes;
};

/**
 * Returns a formatted diff string for easy logging
 * @param {Object} oldObj - Old object 
 * @param {Object} newObj - New object
 * @param {Object} options - Options for computeDiff
 * @returns {string} Formatted diff string
 */
export const getDiffString = (oldObj, newObj, options = {}) => {
  const changes = computeDiff(oldObj, newObj, options);
  if (changes.length === 0) return 'No changes detected';
  return changes.join('\n');
};

/**
 * Check if two objects are deeply different in a meaningful way
 * @param {Object} oldObj - Old object
 * @param {Object} newObj - New object
 * @returns {boolean} True if objects are different
 */
export const hasDeepChanges = (oldObj, newObj) => {
  if (!oldObj || !newObj) return true;
  
  const changes = computeDiff(oldObj, newObj);
  return changes.length > 0;
};

const diffUtils = {
  computeDiff,
  getDiffString,
  hasDeepChanges
};

export default diffUtils; 